import { rcFrame, rcStemBackXAt, rcStemFrontXAt, type Pt, type RcBarPosition, type WallInput, type WallResult } from "@tasduvar/engine";

/** Donatı şeması ögesi: çizgi (kesitte görünen çubuk) ya da noktalar (kesite dik çubuklar); etiket. Şematiktir — çubuk sayısı ölçekli değildir. */
export interface RebarItem { position: RcBarPosition | "stemMainCut"; path?: Pt[]; dots?: Pt[]; label: string; labelAt: Pt; anchor: "start" | "end" | "middle" }

const fmtCm = (s: number): string => `${(Math.round(s * 1000) / 10).toString().replace(".", ",")}`;
const barLabel = (dia: number, spacing: number): string => `φ${dia}/${fmtCm(spacing)}`;

/**
 * Betonarme konsol duvar donatı şeması (model koordinatları, m). Ana çubuklar pas payı kadar içeride; gövde arka çubuğu tabana kanca ile
 * (burun yönünde l_b), yarısı kesme kotu + uzatmada biter; burun altı ve topuk üstü çubukları gövdeye kadar; dağıtma/yatay donatı nokta olarak.
 */
export function rebarItems(wall: WallInput, result: WallResult): RebarItem[] {
  const rc = result.rc, g = wall.geometry;
  if (!rc || g.mode !== "rc-cantilever") return [];
  const f = rcFrame(g);
  const c = rc.cover;
  const byPos = new Map(rc.reinforcement.map((r) => [r.position, r]));
  const items: RebarItem[] = [];
  const stemMain = byPos.get("stemMain");
  if (stemMain) {
    const hook = Math.min(stemMain.lb, f.B - 2 * c - (f.B - f.xStemBack));   // kanca burun yönünde, tabanın içinde kalsın
    const xBot = f.xStemBack - c, xTop = rcStemBackXAt(g, f.yTop) - c;
    items.push({ position: "stemMain", path: [{ x: Math.max(c, xBot - hook), y: c }, { x: xBot, y: c }, { x: xBot, y: f.tf }, { x: xTop, y: f.yTop - c }], label: barLabel(stemMain.bar.dia, stemMain.bar.spacing), labelAt: { x: xTop + 0.08, y: f.tf + 0.35 * f.hs }, anchor: "start" });
    if (stemMain.cutoff) {
      const yCut = Math.min(f.yTop - c, stemMain.cutoff.level + stemMain.cutoff.extend);
      const dx = 0.04;
      items.push({ position: "stemMainCut", path: [{ x: xBot - dx, y: c + 0.03 }, { x: xBot - dx, y: f.tf }, { x: rcStemBackXAt(g, yCut) - c - dx, y: yCut }], label: `½ ${barLabel(stemMain.bar.dia, stemMain.bar.spacing * 2)} ↑${fmtCm(yCut).replace(",", ".")} m`.replace(`${fmtCm(yCut).replace(",", ".")} m`, `${(Math.round(yCut * 100) / 100).toString().replace(".", ",")} m`), labelAt: { x: rcStemBackXAt(g, yCut) - c - dx + 0.08, y: yCut }, anchor: "start" });
    }
  }
  const front = byPos.get("stemFront");
  if (front) {
    const xBot = f.xStemFront + c, xTop = rcStemFrontXAt(g, f.yTop) + c;
    items.push({ position: "stemFront", path: [{ x: xBot, y: c + 0.03 }, { x: xBot, y: f.tf }, { x: xTop, y: f.yTop - c }], label: barLabel(front.bar.dia, front.bar.spacing), labelAt: { x: xTop - 0.08, y: f.tf + 0.6 * f.hs }, anchor: "end" });
  }
  const hor = byPos.get("stemHorizontal");
  if (hor) {
    const dots: Pt[] = [];
    for (let y = f.tf + c + 0.05; y < f.yTop - c; y += hor.bar.spacing) dots.push({ x: rcStemBackXAt(g, y) - c - 0.02, y }, { x: rcStemFrontXAt(g, y) + c + 0.02, y });
    items.push({ position: "stemHorizontal", dots, label: `yatay ${barLabel(hor.bar.dia, hor.bar.spacing)}`, labelAt: { x: rcStemFrontXAt(g, f.tf + 0.85 * f.hs) - 0.08, y: f.tf + 0.85 * f.hs }, anchor: "end" });
  }
  const toe = byPos.get("toeMain");
  if (toe && f.toe > 0) {
    const y = toe.face === "alt yüz" ? c + 0.02 : f.tf - c - 0.02;
    items.push({ position: "toeMain", path: [{ x: c, y: f.tf - c }, { x: c, y }, { x: Math.min(f.B - c, f.xStemBack + toe.lb), y }], label: barLabel(toe.bar.dia, toe.bar.spacing), labelAt: { x: Math.max(0.05, f.toe / 2 - 0.25), y: f.tf + 0.16 }, anchor: "start" });
  }
  const heel = byPos.get("heelMain");
  if (heel && f.heel > 0) {
    const y = heel.face === "üst yüz" ? f.tf - c - 0.02 : c + 0.02;
    items.push({ position: "heelMain", path: [{ x: Math.max(c, f.xStemFront - heel.lb), y }, { x: f.B - c, y }, { x: f.B - c, y: y > f.tf / 2 ? c : f.tf - c }], label: barLabel(heel.bar.dia, heel.bar.spacing), labelAt: { x: f.B - 0.1, y: f.tf + 0.4 }, anchor: "end" });
  }
  const dist = byPos.get("baseDistribution");
  if (dist) {
    const dots: Pt[] = [];
    for (let x = c + 0.08; x < f.B - c; x += dist.bar.spacing) dots.push({ x, y: c + 0.06 }, { x, y: f.tf - c - 0.06 });
    // Etiket taban plağının içinde, iki nokta sırasının arasında (topuk varsa topuk ortası) — burun etiketiyle çakışmasın
    const xMid = f.heel > 0.6 ? f.xStemBack + f.heel / 2 : f.B / 2;
    items.push({ position: "baseDistribution", dots, label: `boyuna ${barLabel(dist.bar.dia, dist.bar.spacing)}`, labelAt: { x: xMid, y: f.tf / 2 }, anchor: "middle" });
  }
  const key = byPos.get("keyMain");
  if (key && g.key) {
    const x0 = g.key.offsetFromToe + c, x1 = g.key.offsetFromToe + g.key.width - c;
    items.push({ position: "keyMain", path: [{ x: x0, y: c + 0.03 }, { x: x0, y: -g.key.depth + c }, { x: x1, y: -g.key.depth + c }, { x: x1, y: c + 0.03 }], label: barLabel(key.bar.dia, key.bar.spacing), labelAt: { x: x1 + 0.08, y: -g.key.depth / 2 }, anchor: "start" });
  }
  return items;
}
